import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Wordmark } from "@/components/Logo";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

export default function SecuritySetup() {
  const { refreshUser } = useAuth();
  const nav = useNavigate();
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    api.post("/auth/mfa/setup").then((r) => setSetup(r.data)).catch((e) => toast.error(formatError(e)));
  }, []);

  const enable = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/mfa/enable", { code });
      await refreshUser();
      toast.success("2-step verification enabled");
      nav("/admin");
    } catch (err) { toast.error(formatError(err)); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid place-items-center p-6 bg-[#09090D]">
      <Card className="w-full max-w-md bg-zinc-950 border-amber-500/20 gold-glow">
        <CardContent className="p-8">
          <Wordmark className="mb-6" />
          <h1 className="font-display text-2xl font-black uppercase mb-1">Secure your admin login</h1>
          <p className="text-sm text-zinc-400 mb-6">Scan this QR with Google Authenticator (or any authenticator app), then enter the 6-digit code.</p>
          {setup && (
            <div className="flex flex-col items-center gap-3 mb-6" data-testid="mfa-qr">
              <div className="bg-white p-3 rounded"><QRCodeSVG value={setup.otpauth_uri} size={176} /></div>
              <div className="text-[11px] text-zinc-500">Can't scan? Enter this key manually:</div>
              <code className="font-mono-stat text-amber-300 text-xs break-all" data-testid="mfa-secret">{setup.secret}</code>
            </div>
          )}
          <form onSubmit={enable} className="space-y-4">
            <Input inputMode="numeric" maxLength={6} placeholder="6-digit code" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} className="bg-black border-amber-500/20 text-center tracking-[0.5em] font-mono-stat text-lg" data-testid="mfa-setup-code" />
            <Button disabled={busy || code.length !== 6} className="w-full bg-gradient-to-r from-amber-300 via-amber-400 to-amber-600 text-black font-display uppercase tracking-widest" data-testid="mfa-enable-btn">
              {busy ? "Verifying…" : "Enable"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
